import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import { Link, useNavigate, useParams } from "react-router-dom";

import { styled, Tooltip } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import MuiTab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";

import CloseIcon from "@mui/icons-material/Close";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import { BadgeCheck, Globe, Star } from "lucide-react";

import Axios from "../../Assets/Axios/Axios";

import Reveal from "../../Components/Reveal";
import { getCategoryIcon } from "../../Helpers/categoryIcon";

import PackageCard from "./Components/PackageCard.jsx";
import SingleReview from "./Components/SingleReview";
import Footer from "../../Components/Footer";
import LoginModal from "./../../Components/Modals/Login";
import SignUpModal from "./../../Components/Modals/Signup";
import Header from "../../Components/Header";
import ImageGallery, { ImageGallerySkeleton } from "./Components/ImageGallery";

import { COLORS } from "../../Constant/COLORS";
import { USER_TYPES } from "../../Constant/Enums";

import ToastContext from "../../Context/ToastContext";
import { useAuthContext } from "../../Context/AuthContext";

import { toCapitalize, toCurrencyString } from "../../Helpers";

import BookingModal from "./Modals/Booking";

import { SellerProfileQuery } from "../../Server/Query";

import "./index.css";
import { useIsMobile } from "../../Hooks/useIsMobile";

const Seller = () => {
  const [service, setService] = useState({
    id: "",
    serviceName: "",
    avatarURL: "",
    serviceDescription: "",
    serviceLink: "",
    categorySlug: "",
    categoryName: "",
  });
  const [professional, setProfessional] = useState({
    firstName: "",
    lastName: "",
    about: "",
  });
  const [servicePackages, setServicePackages] = useState(null);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [reviews, setReviews] = useState(null);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoginModalVisible, setIsLoginModalVisible] = useState(false);
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [images, setImages] = useState([]);

  const [isLoading, setIsLoading] = useState(true);

  const sectionsRef = useRef({
    aboutSection: "",
    packageSection: "",
    reviewsSection: "",
  }).current;

  const { user } = useAuthContext();
  const { showToastMessage } = useContext(ToastContext);
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const selectedPackage = useMemo(() => {
    if (servicePackages !== null && selectedPackageId !== null) {
      let selectedServicePackage = servicePackages.find(
        (servicePackage) => servicePackage.id === selectedPackageId,
      );
      return selectedServicePackage ?? null;
    }
    return null;
  }, [selectedPackageId, servicePackages]);

  useEffect(() => {
    if (!serviceId) return undefined;

    let active = true;
    setIsLoading(true);

    Axios(SellerProfileQuery({ serviceId }))
      .then(({ data, errors }) => {
        if (!active) return undefined;

        if (errors) {
          showToastMessage("Something went wrong unable get service details");
          return undefined;
        }

        const profile = data?.sellerProfile;

        // Missing service, or a professional that isn't accepted/active.
        if (!profile) {
          navigate("/not-found", { replace: true });
          return undefined;
        }

        const { service, professional, servicePackages, reviews } = profile;

        setService({
          // UUID — used for booking (orders.service_id FK). Not shown in the URL.
          id: service.id,
          serviceLink: service.websiteUrl,
          avatarURL: service.icon,
          serviceDescription: service.description,
          serviceName: service.name,
          categorySlug: service.categorySlug ?? "",
          categoryName: service.categoryName ?? "",
        });

        setProfessional({
          about: professional.about,
          firstName: professional.firstName,
          lastName: professional.lastName ?? "",
        });

        setServicePackages(servicePackages ?? []);

        setReviews(
          (reviews ?? []).map((review) => ({
            id: review.id,
            description: review.description,
            reviewer: review.reviewer ?? "Verified customer",
          }))
        );

        setImages(service.images ?? []);

        return undefined;
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [navigate, serviceId, showToastMessage]);


  function handleClickHire() {
    if (!user || user.type !== USER_TYPES.USER) {
      setIsLoginModalVisible(true);
      return undefined;
    }

    setIsModalVisible(true);
  }

  function handleClickTabs(event) {
    const elementId = event.currentTarget.dataset.id;
    const scrollTop =
      elementId === "overviewSection" ? 0 : sectionsRef[elementId].offsetTop;

    window.scrollTo({ behavior: "smooth", top: scrollTop });
  }

  function handleClickPackage(packageId) {
    setSelectedPackageId(packageId);
  }

  function openSignUpModalFromLogin() {
    setIsLoginModalVisible(false);
    setIsSignUpModalOpen(true);
  }

  function openLoginModalFromSignup() {
    setIsSignUpModalOpen(false);
    setIsLoginModalVisible(true);
  }

  return (
    <>
      <Header isSellerOrCategoryPage />
      <Divider style={{ borderColor: COLORS.hairline }} />
      <Box
        px={{ xs: 2, md: undefined }}
        maxWidth={{ md: 4 / 5 }}
        mx="auto"
        mt={3}
      >
        <Breadcrumbs
          separator={<NavigateNextIcon />}
          sx={{ mb: 2 }}
          itemsBeforeCollapse={3}
        >
          <Typography
            variant="body1"
            fontWeight={600}
            component={Link}
            to={`/category/${service.categorySlug}`}
            sx={{ textDecoration: "none", color: COLORS.teal }}
          >
            {toCapitalize(service.categoryName || service.serviceName)}
          </Typography>

          <Typography fontWeight={500}>{professional.firstName}</Typography>
        </Breadcrumbs>

        <Tabs
          sx={{ maxWidth: "100%" }}
          variant="scrollable"
          TabIndicatorProps={{ sx: { bgcolor: COLORS.teal, height: "3px", borderRadius: "3px" } }}
          value={selectedTab}
          onChange={(_event, value) => setSelectedTab(value)}
        >
          {tabs.map((tab) => (
            <Tab key={tab.label} onClick={handleClickTabs} {...tab} />
          ))}
        </Tabs>
      </Box>
      <Divider style={{ borderBottom: `1px solid ${COLORS.hairline}` }} />
      <Box
        px={{ xs: 2, md: "unset" }}
        maxWidth={{ md: 4 / 5 }}
        mx="auto"
        mb={8}
      >
        <Box maxWidth={{ md: 3 / 5 }} mb={5} mt={3.5}>
          <Box
            display="grid"
            gap={2}
            alignItems="center"
            mb={3}
            mt={4}
            width="100%"
            gridTemplateColumns={{
              xs: "auto 1fr",
              sm: "auto 1fr auto",
            }}
          >
            <Box sx={{ position: "relative", width: 66, height: 66 }}>
              <Avatar src={service.avatarURL} sx={{ width: 66, height: 66 }} />
              <Box
                sx={{
                  position: "absolute",
                  right: -2,
                  bottom: -2,
                  bgcolor: "#fff",
                  borderRadius: "50%",
                  display: "flex",
                  lineHeight: 0,
                }}
              >
                <BadgeCheck size={22} color={COLORS.teal} fill={COLORS.tealSoft} />
              </Box>
            </Box>
            <Box flexGrow={1} minWidth={0}>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                component="p"
              >
                {professional.firstName.concat(" ", professional.lastName)}
              </Typography>
              <Stack
                direction="row"
                flexWrap="wrap"
                alignItems="center"
                gap={1}
                mt={1}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={0.5}
                  sx={{
                    px: 1,
                    py: 0.4,
                    borderRadius: 999,
                    bgcolor: COLORS.tealSoft,
                  }}
                >
                  {(() => {
                    const CategoryIcon = getCategoryIcon(service.serviceName);
                    return <CategoryIcon size={13} color={COLORS.teal} strokeWidth={1.9} />;
                  })()}
                  <Typography variant="caption" fontWeight={600} color={COLORS.teal}>
                    {toCapitalize(service.serviceName)}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={0.5}
                  sx={{ color: COLORS.inkSoft }}
                >
                  <Star size={14} color={COLORS.brass} fill={COLORS.brass} />
                  <Typography variant="caption" fontWeight={600}>
                    Highly rated
                  </Typography>
                </Stack>
              </Stack>
            </Box>
            <Tooltip
              title={
                user?.type === USER_TYPES.PROFESSIONAL
                  ? "Login as a user to hire"
                  : ""
              }
            >
              {/* span wrapper: a disabled MUI Button swallows hover events, so
                  the Tooltip wouldn't show without an enabled element to attach to. */}
              <Box
                component="span"
                sx={{
                  width: "min(150px,100%)",
                  justifySelf: "end",
                  gridColumnStart: { xs: "2", sm: "unset" },
                }}
              >
                <Button
                  onClick={handleClickHire}
                  disabled={user?.type === USER_TYPES.PROFESSIONAL}
                  color="green"
                  sx={{
                    width: "100%",
                    color: "white",
                  }}
                >
                  Hire
                </Button>
              </Box>
            </Tooltip>
          </Box>

          {isLoading ? (
            <ImageGallerySkeleton />
          ) : (
            <ImageGallery images={images} />
          )}
        </Box>
        <Box
          maxWidth="95%"
          mb={6}
          ref={(el) => (sectionsRef.aboutSection = el)}
        >
          <Typography variant="h4" fontWeight={600} mb={{ xs: 3, md: 6 }}>
            About Seller
          </Typography>
          <Typography fontWeight={500} lineHeight="21px" textAlign="justify">
            {professional.about}
          </Typography>

          {service.serviceLink && (
            <Box mt={2.5} width="fit-content" ml="auto">
              <Button
                component="a"
                href={service.serviceLink}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                startIcon={<Globe size={16} strokeWidth={1.8} />}
                sx={{ color: COLORS.teal }}
              >
                Visit Website
              </Button>
            </Box>
          )}

          <Typography
            ref={(el) => (sectionsRef.packageSection = el)}
            fontWeight={600}
            variant="h5"
            mt={{ xs: 2, md: 4 }}
            mb={{ xs: 1, md: 2 }}
          >
            Packages Details
          </Typography>

          {servicePackages?.length > 0 ? (
            <Box
              display="grid"
              gridTemplateColumns={{ md: "repeat(3,1fr)" }}
              gap={2}
            >
              {servicePackages.map((servicePackage) => (
                <PackageCard
                  key={servicePackage.id}
                  description={servicePackage.description}
                  price={servicePackage.price}
                  features={servicePackage.features}
                  title={servicePackage.name}
                  onClick={() => handleClickPackage(servicePackage.id)}
                />
              ))}
            </Box>
          ) : (
            <Typography fontWeight={500}>
              There aren&apos;t any packages available for this service yet
            </Typography>
          )}

          {selectedPackage && (
            <Stack
              direction="row"
              mt={3}
              px={3}
              py={2}
              borderRadius={2}
              bgcolor={COLORS.surfaceAlt}
              border={`1px solid ${COLORS.hairline}`}
            >
              <Box
                flexGrow="1"
                display="grid"
                gridTemplateColumns="auto 1fr"
                columnGap={2}
                alignItems="center"
              >
                <Typography variant="h6">Package Name</Typography>
                <Typography variant="body1">{selectedPackage.name}</Typography>
                <Typography variant="h6">Price</Typography>
                <Typography variant="body1">
                  {toCurrencyString(selectedPackage.price)}
                </Typography>
                <Typography variant="h6" alignSelf="flex-start">
                  Description
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                  {selectedPackage.description}
                </Typography>
              </Box>
              <IconButton
                sx={{ alignSelf: "flex-start" }}
                onClick={() => setSelectedPackageId(null)}
              >
                <CloseIcon htmlColor="black" />
              </IconButton>
            </Stack>
          )}
        </Box>

        <Typography
          variant="h4"
          fontWeight={600}
          mb={{ xs: 2, md: 4 }}
          ref={(el) => (sectionsRef.reviewsSection = el)}
        >
          Reviews
        </Typography>

        {reviews?.length > 0 ? (
          <Grid container spacing={{ xs: 2, md: 6 }} maxWidth={{ md: "90%" }}>
            {reviews.map((review, index) => (
              <Grid item lg={6} xs={12} key={review.id}>
                <Reveal delay={Math.min(index, 5) * 0.06} style={{ height: "100%" }}>
                  <SingleReview
                    desc={review.description}
                    name={review.reviewer}
                  />
                </Reveal>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography fontWeight={500} mt={{ xs: 2, md: 4 }}>
            There aren&apos;t any reviews for this service yet
          </Typography>
        )}
      </Box>
      <Footer />

      <BookingModal
        open={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        serviceId={service.id}
        servicePackages={servicePackages}
      />

      <LoginModal
        open={isLoginModalVisible}
        onClose={() => setIsLoginModalVisible(false)}
        onClickSignUpNowText={openSignUpModalFromLogin}
      />

      <SignUpModal
        open={isSignUpModalOpen}
        openLoginModal={openLoginModalFromSignup}
        onClose={() => setIsSignUpModalOpen(false)}
      />
    </>
  );
};

const tabs = [
  { label: "Overview", "data-id": "overviewSection" },
  {
    label: "About The Seller",
    "data-id": "aboutSection",
  },
  { label: "Pricing", "data-id": "packageSection" },
  { label: "Reviews", "data-id": "reviewsSection" },
];

const Tab = styled(MuiTab)({
  textTransform: "none",
  color: COLORS.inkSoft,
  fontWeight: 600,
  opacity: "1",
  "&.Mui-selected": { color: COLORS.teal },
});

export default Seller;
